import { useState, type FormEvent } from 'react';
import { BrandMark } from './BrandMark.js';

interface LoginScreenProps {
  onLogin(email: string, password: string): Promise<void>;
  onRequestRecovery(email: string): Promise<void>;
  pending: boolean;
  message?: string;
  sessionExpired?: boolean;
  recoveryDeliveryEnabled?: boolean;
}

export function LoginScreen({
  onLogin,
  onRequestRecovery,
  pending,
  message,
  sessionExpired,
  recoveryDeliveryEnabled = false,
}: LoginScreenProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [recoveryPending, setRecoveryPending] = useState(false);
  const [recoverySent, setRecoverySent] = useState(false);
  const [recoveryUnavailable, setRecoveryUnavailable] = useState(false);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!pending) void onLogin(email.trim(), password);
  };
  const requestRecovery = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (recoveryPending) return;
    setRecoveryPending(true);
    try {
      await onRequestRecovery(email.trim());
    } catch {
      // Recovery responses stay generic even when the provider cannot confirm delivery.
    } finally {
      setRecoveryPending(false);
      setRecoverySent(true);
    }
  };

  return (
    <main className="login-layout">
      <section className="login-story" aria-label="Raptor Pharmacy OS introduction">
        <div className="brand-lockup brand-lockup--hero">
          <BrandMark />
          <div><strong>RAPTOR</strong><span>PHARMACY OS</span></div>
        </div>
        <div className="login-story__geometry" aria-hidden="true"><span /><span /><span /></div>
        <div className="login-story__copy">
          <p className="eyebrow eyebrow--light">PHARMACY OS</p>
          <h1 aria-label="Build. Automate. Scale."><span aria-hidden="true">Build.</span><span aria-hidden="true">Automate.</span><span aria-hidden="true">Scale.</span></h1>
        </div>
        <div className="login-story__index" aria-hidden="true"><span>01</span><i /><span>OS</span></div>
      </section>
      <section className="login-panel">
        <div className="login-form-wrap">
          <div className="login-panel__mark"><BrandMark /><span>RAPTOR</span></div>
          <p className="eyebrow">{recoveryMode ? 'Password recovery' : 'PHARMACY OS'}</p>
          <h2>{recoveryMode ? 'Reset access' : 'Welcome back'}</h2>
          <p className="form-intro">{recoveryMode ? 'Enter your approved workspace email.' : 'Sign in to continue to your workspace'}</p>
          {sessionExpired && <div id="session-notice" className="notice" role="status">Your session ended. Sign in again to continue.</div>}
          {message && <div id="login-error" className="notice notice--error" role="alert">{message}</div>}
          {recoveryMode ? (
            <form onSubmit={(event) => void requestRecovery(event)} aria-describedby="recovery-help">
              <label htmlFor="recovery-email">Email address</label>
              <input id="recovery-email" name="email" type="email" autoComplete="email" inputMode="email" required disabled={recoveryPending || recoverySent} value={email} onChange={(event) => setEmail(event.target.value)} />
              {recoverySent && <div className="notice" role="status">If an account exists for this email, password reset instructions have been sent.</div>}
              {!recoverySent && <button className="primary-action" type="submit" disabled={recoveryPending}>{recoveryPending ? 'Requesting…' : 'Send reset instructions'}</button>}
              <button className="text-action login-alternate" type="button" onClick={() => { setRecoveryMode(false); setRecoverySent(false); }}>Return to sign in</button>
              <p id="recovery-help" className="privacy-note">For privacy, the response is the same whether or not an account exists.</p>
            </form>
          ) : <form
            onSubmit={submit}
            aria-describedby={[
              sessionExpired ? 'session-notice' : '',
              message ? 'login-error' : '',
              recoveryUnavailable ? 'login-help' : '',
            ].filter(Boolean).join(' ') || undefined}
          >
            <label htmlFor="email">Email address</label>
            <input id="email" name="email" type="email" autoComplete="email" inputMode="email" required disabled={pending} value={email} onChange={(event) => setEmail(event.target.value)} placeholder="name@pharmacy.example" />
            <div className="form-label-row">
              <label htmlFor="password">Password</label>
              <button
                className="text-action login-alternate"
                type="button"
                onClick={() => {
                  setRecoveryUnavailable(false);
                  if (recoveryDeliveryEnabled) setRecoveryMode(true);
                  else setRecoveryUnavailable(true);
                }}
              >Forgot password?</button>
            </div>
            <div className="password-field">
              <input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" required disabled={pending} value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Enter your password" />
              <button type="button" className="password-toggle" onClick={() => setShowPassword((visible) => !visible)} aria-label={showPassword ? 'Hide password' : 'Show password'}>{showPassword ? 'Hide' : 'Show'}</button>
            </div>
            {recoveryUnavailable && <div id="login-help" className="notice" role="status">Password recovery will be available when email delivery is enabled.</div>}
            <button className="primary-action" type="submit" disabled={pending}>{pending ? 'Signing in…' : 'Log In'}</button>
          </form>}
        </div>
      </section>
    </main>
  );
}
